import { OrgReorderChildrenDialogComponent } from '../dialogs/org-reorder-children-dialog/org-reorder-children-dialog.component';
import { OrgSecurityGroupsListDialogComponent } from '../dialogs/org-security-groups-list-dialog/org-security-groups-list-dialog';
import { OrgPositionsListDialogComponent } from '../dialogs/org-positions-list-dialog/org-positions-list-dialog';
import {
    OrgFeaturesPermissionsListDialogComponent,
    FeaturesPermissionsListMode,
} from '../dialogs/org-features-permissions-list-dialog/org-features-permissions-list-dialog';
import {
    PolicyAcknowledgmentDialogComponent,
    PolicyAcknowledgmentDialogData,
    PolicyAcknowledgmentDialogResult,
} from '../dialogs/org-policy-acknowledgment-dialog/org-policy-acknowledgment-dialog';
import { OrgPoliciesListDialogComponent } from '../dialogs/org-policies-list-dialog/org-policies-list-dialog';
import { OrgMyPoliciesDialogComponent } from '../dialogs/org-my-policies-dialog/org-my-policies-dialog';
import { OrgUsersListDialogComponent } from '../dialogs/org-users-list-dialog/org-users-list-dialog';
import { OrgHatsListDialogComponent } from '../dialogs/org-hats-list-dialog/org-hats-list-dialog';
import { OrgHatDetailsDialogComponent } from '../dialogs/org-hat-details-dialog/org-hat-details-dialog';
import { OrgSecurityGroupDetailsDialogComponent } from '../dialogs/org-security-group-details-dialog/org-security-group-details-dialog';
import { OrgPolicyDetailsDialogComponent } from '../dialogs/org-policy-details-dialog/org-policy-details-dialog';
import { OrgHierarchyDialogComponent } from '../dialogs/org-position-dialog/org-hierarchy-dialog/org-hierarchy-dialog';
import { OrgNotificationListDialogComponent } from '../dialogs/og-notification-dialog/org-notification-list-dialog';
import { OrgAssignSecurityGroupsDialogComponent } from '../dialogs/org-assign-security-groups-dialog';
import { OrgNotificationDialogComponent } from '../dialogs/og-notification-dialog/org-notification-dialog';
import { OrgPositionDialogComponent } from '../dialogs/org-position-dialog/org-position-dialog';
import { OrgSecurityGroupComponent } from '../dialogs/org-security-group/org-security-group';
import { OrgNewHatDialogComponent } from '../dialogs/org-new-hat-dialog/org-new-hat-dialog';
import { OrgPolicyDialogComponent } from '../dialogs/org-policy-dialog/org-policy-dialog';
import {
    ECONode,
    Organization,
    OrgHat,
    OrgPolicy,
    OrgSecurityGroup,
    PendingPositionApproval,
    PolicyAcknowledgment,
    SecurityTask,
} from '@newgenus/common';
import { OrgUserDetailsDialogComponent } from '../dialogs/org-user-details-dialog';
import { OrgAssignUserDialogComponent } from '../dialogs/org-assign-user-dialog';
import { OrgHatDialogComponent } from '../dialogs/org-hat-dialog/org-hat-dialog';
import {
    OrgInviteUserDialogComponent,
    InviteUserDialogData,
} from '../dialogs/org-invite-user-dialog/org-invite-user-dialog';
import { InviteUserDialogResult } from '@newgenus/common';
import {
    OrgSelectHatDialogComponent,
    SelectHatDialogInput,
    SelectHatDialogResult,
} from '../dialogs/org-select-hat-dialog/org-select-hat-dialog';
import {
    OrgApproveAssignmentDialogComponent,
    ApproveAssignmentDialogData,
} from '../dialogs/org-approve-assignment-dialog/org-approve-assignment-dialog';
import {
    OrgPostHatsManagerDialogComponent,
    PostHatsManagerDialogData,
} from '../dialogs/org-post-hats-manager-dialog/org-post-hats-manager-dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrgBoardService } from './org-board-service';
import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { BehaviorSubject, take, firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class OrgDialogService {
    /**
     * The organization to be used in the dialog.
     * This should be set before opening the dialog, preferably in the parent/main org board component.
     * Uses a BehaviorSubject so open dialogs can subscribe to live updates.
     *
     * @type {(Organization | undefined)}
     * @memberof OrgDialogService
     */
    private _organization: Organization | undefined;
    public organization$ = new BehaviorSubject<Organization | undefined>(undefined);

    public get organization(): Organization | undefined {
        return this._organization;
    }
    public set organization(value: Organization | undefined) {
        this._organization = value;
        this.organization$.next(value);
    }

    /**
     * The API base URL for backend function calls.
     * This should be set by the host application using environment configuration.
     * Example: 'https://us-central1-newgenus-dev-19da7.cloudfunctions.net/api-'
     *
     * When set, policy publishing will use the backend API for proper target resolution.
     * When not set, direct Firestore updates will be used as a fallback.
     *
     * Setting this also propagates the value to OrgBoardService for invitation API calls.
     */
    private _apiBaseUrl: string | undefined;
    public get apiBaseUrl(): string | undefined {
        return this._apiBaseUrl;
    }
    public set apiBaseUrl(value: string | undefined) {
        this._apiBaseUrl = value;
        this.obService.apiBaseUrl = value;
    }

    private hatSecurityTasksSub$ = new BehaviorSubject<SecurityTask[]>([]);
    public hatSecurityTasks$ = this.hatSecurityTasksSub$.asObservable();

    /** Currently online user IDs — set by the host (e.g. org-landing) */
    public onlineUserIds: string[] = [];

    /** Permission flags — set by the host org-board component from SecurityService checks */
    public mayEdit = false;
    public mayManageUsers = false;
    public mayManageHats = false;
    public mayManageSecurityGroups = false;
    public mayManagePolicies = false;
    public isDev = false;

    constructor(
        public dialog: MatDialog,
        private snackbar: MatSnackBar,
        private obService: OrgBoardService
    ) {}

    public updateHatSecurityTasks(tasks: SecurityTask[]): void {
        this.hatSecurityTasksSub$.next(tasks);
    }

    /**
     * Opens the Users List dialog showing all organization users.
     * Users can search, view details, and perform actions on users.
     */
    public openUsersListDialog(positionFilter?: string, onlyKeys?: string[]): void {
        console.log('OrgDialogService > openUsersListDialog > organization:', this.organization);
        this.dialog
            .open(OrgUsersListDialogComponent, {
                data: {
                    organization: this.organization,
                    positionFilter,
                    onlyKeys,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result?.action === 'view' && result?.uid) {
                    // Create a minimal UserDetails-compatible object with required 'key' property
                    const userForDialog = {
                        key: result.uid,
                        name: result.user?.name || result.user?.userName || 'Unknown User',
                        ...result.user,
                    };
                    this.openOrgUserDetailsDialog(null as any, userForDialog);
                }
            });
    }

    /**
     * Opens the Hats List dialog showing all organization hats.
     * Users can search, view details, and see assigned users, security groups, etc.
     */
    public openHatsListDialog(positionFilter?: string, onlyKeys?: string[]): void {
        console.log('OrgDialogService > openHatsListDialog > organization:', this.organization);
        this.dialog
            .open(OrgHatsListDialogComponent, {
                data: {
                    organization: this.organization,
                    positionFilter,
                    onlyKeys,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                console.log('Hats list dialog closed:', result);
            });
    }

    /**
     * Opens the Security Groups List dialog showing all organization security groups.
     * Users can search, view details, and see features, permissions, hats using group, etc.
     */
    public openSecurityGroupsListDialog(positionFilter?: string, onlyKeys?: string[]): void {
        console.log('OrgDialogService > openSecurityGroupsListDialog > organization:', this.organization);
        this.dialog
            .open(OrgSecurityGroupsListDialogComponent, {
                data: {
                    organization: this.organization,
                    positionFilter,
                    onlyKeys,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                console.log('Security Groups list dialog closed:', result);
            });
    }

    /**
     * Opens the Features or the Permissions list (the Overview's Features / Permissions tiles,
     * ISS-024): each feature or permission with the security groups that grant it and the hats
     * and staff who end up with it.
     */
    public openFeaturesPermissionsListDialog(mode: FeaturesPermissionsListMode, onlyKeys?: string[]): void {
        this.dialog.open(OrgFeaturesPermissionsListDialogComponent, {
            data: {
                organization: this.organization,
                mode,
                onlyKeys,
            },
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
            panelClass: 'ob-list-dialog',
        });
    }

    /**
     * Opens the Positions List dialog showing all organization positions.
     * Users can search, view details, and see position hierarchy, hats, users at position, etc.
     */
    public openPositionsListDialog(onlyKeys?: string[]): void {
        console.log('OrgDialogService > openPositionsListDialog > organization:', this.organization);
        this.dialog
            .open(OrgPositionsListDialogComponent, {
                data: {
                    organization: this.organization,
                    onlyKeys,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                console.log('Positions list dialog closed:', result);
            });
    }

    /**
     * Opens the Policies List dialog showing all organization policies.
     * Users can search, view details, and see policy content, hats using policy, revisions, etc.
     */
    public openPoliciesListDialog(positionFilter?: string, onlyKeys?: string[]): void {
        console.log('OrgDialogService > openPoliciesListDialog > organization:', this.organization);
        this.dialog
            .open(OrgPoliciesListDialogComponent, {
                data: {
                    organization: this.organization,
                    positionFilter,
                    onlyKeys,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                console.log('Policies list dialog closed:', result);
            });
    }

    /**
     * Opens the My Policies dialog showing policies assigned to the current user.
     * Shows pending acknowledgments and previously acknowledged policies.
     */
    public openMyPoliciesDialog(): void {
        console.log('OrgDialogService > openMyPoliciesDialog > organization:', this.organization);
        this.dialog
            .open(OrgMyPoliciesDialogComponent, {
                data: {
                    organization: this.organization,
                },
                height: '85%',
                width: '70%',
                maxHeight: 'unset',
                maxWidth: 'unset',
                panelClass: 'ob-list-dialog',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                console.log('My Policies dialog closed:', result);
            });
    }

    /**
     * Opens the Policy Acknowledgment dialog for a specific policy.
     * Allows user to read, understand, and agree to the policy.
     * Returns a promise that resolves when the dialog is closed.
     */
    public openPolicyAcknowledgmentDialog(
        acknowledgment: PolicyAcknowledgment,
        policy?: OrgPolicy
    ): Promise<PolicyAcknowledgmentDialogResult | null> {
        const dialogData: PolicyAcknowledgmentDialogData = {
            organization: this.organization!,
            acknowledgment,
            policy,
        };

        return firstValueFrom(
            this.dialog
                .open(PolicyAcknowledgmentDialogComponent, {
                    data: dialogData,
                    width: '700px',
                    maxHeight: '90vh',
                    disableClose: false,
                })
                .afterClosed()
                .pipe(take(1))
        );
    }

    public openOrgUserDetailsDialog(node: ECONode<any>, user: any): void {
        this.dialog
            .open(OrgUserDetailsDialogComponent, {
                data: {
                    node,
                    user,
                    organization: this.organization,
                    onlineUserIds: this.onlineUserIds,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    /**
     * Opens the Hat Details dialog for a specific hat.
     * Use this when you have the hat data available (e.g. from dashboard widgets).
     */
    public openHatDetailsDialog(hatKey: string, hat: any): void {
        this.dialog.open(OrgHatDetailsDialogComponent, {
            data: {
                hat,
                hatKey,
                organization: this.organization,
            },
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
        });
    }

    /**
     * Opens the Security Group Details dialog for a specific security group.
     * Use this when you have the SG data available (e.g. from dashboard widgets).
     */
    public openSecurityGroupDetailsDialog(sgKey: string, securityGroup: any): void {
        this.dialog.open(OrgSecurityGroupDetailsDialogComponent, {
            data: {
                securityGroup,
                securityGroupKey: sgKey,
                organization: this.organization,
            },
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            panelClass: 'security-group-details-dialog',
        });
    }

    /**
     * Opens the Policy Details dialog for a specific policy.
     * Use this when you have the policy data available (e.g. from dashboard widgets).
     */
    public openPolicyDetailsDialog(policyKey: string, policy: any): void {
        this.dialog.open(OrgPolicyDetailsDialogComponent, {
            data: {
                policy,
                policyKey,
                organization: this.organization,
            },
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            panelClass: 'policy-details-dialog',
        });
    }

    /**
     * Opens the Position Details dialog for a specific position.
     * Use this when you have a position/part object (e.g. from dashboard widgets).
     */
    public openPositionDetailsDialog(position: any): void {
        console.log('openPositionDetailsDialog > position:', position);
        console.log('openPositionDetailsDialog > organization:', this.organization);
        console.log('openPositionDetailsDialog > orgParts:', this.organization?.orgParts?.length);
        import('../dialogs/org-position-details-dialog').then((m) => {
            this.dialog.open(m.OrgPositionDetailsDialogComponent, {
                data: {
                    organization: this.organization,
                    position,
                },
                width: '85%',
                height: '85%',
                maxWidth: 'unset',
                maxHeight: 'unset',
            });
        });
    }

    public openHat(node: ECONode<any>, user?: any): void {
        this.dialog
            .open(OrgHatDialogComponent, {
                data: {
                    node,
                    user,
                    organization: this.organization,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openSecurityGroupsDialog(node: ECONode<any>, user?: any): void {
        this.dialog
            .open(OrgAssignSecurityGroupsDialogComponent, {
                data: {
                    node,
                    user,
                    organization: this.organization,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openAssignUserDialog(node: ECONode<any>, user?: any): void {
        this.dialog
            .open(OrgAssignUserDialogComponent, {
                data: {
                    node,
                    user,
                    organization: this.organization,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openInviteUserDialog(): void {
        const dialogData: InviteUserDialogData = {
            organization: this.organization!,
            apiBaseUrl: this.apiBaseUrl,
            checkEmailStatus: (email: string, orgKey: string) => this.obService.checkEmailStatus(email, orgKey),
            getOrgInvitations: (orgKey: string, status?: any) => this.obService.getOrgInvitations(orgKey, status),
            resendInvitation: (invitationKey: string) => this.obService.resendInvitation(invitationKey),
            revokeInvitation: (invitationKey: string) => this.obService.revokeInvitation(invitationKey),
            deleteInvitation: (invitationKey: string) => this.obService.deleteInvitation(invitationKey),
        };

        this.dialog
            .open<OrgInviteUserDialogComponent, InviteUserDialogData, InviteUserDialogResult>(
                OrgInviteUserDialogComponent,
                {
                    data: dialogData,
                    height: '85%',
                    width: '70%',
                    maxHeight: '900px',
                    maxWidth: '1100px',
                }
            )
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result?.invitations?.length && this.organization) {
                    this.obService.inviteUsersToOrg(this.organization.key, result);
                }
            });
    }

    public openPolicyDialog(goToNewTab = false): void {
        this.dialog
            .open<OrgPolicyDialogComponent, any, Array<string>>(OrgPolicyDialogComponent, {
                data: {
                    organization: this.organization,
                    goToNewTab: goToNewTab,
                    apiBaseUrl: this.apiBaseUrl, // Pass API URL for backend calls
                },
                height: '90vh',
                width: '80%',
                maxHeight: '900px',
                maxWidth: '1200px',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openNewHatDialog(
        node?: ECONode<any>,
        preSelectedUser?: { uid: string; name: string },
        targetPartKey?: string,
        initialTab?: number
    ): void {
        this.dialog
            .open<OrgNewHatDialogComponent, any, Array<string>>(OrgNewHatDialogComponent, {
                data: {
                    organization: this.organization,
                    node: node,
                    preSelectedUser: preSelectedUser,
                    targetPartKey: targetPartKey,
                    initialTab: initialTab,
                },
                height: '85%',
                width: '75%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openSelectPositionDialog(
        node?: ECONode<any>,
        config = {
            selection: 'single' as 'single' | 'multiple',
            allowCreate: true,
        },
        selectedPositionKeys?: Array<string>
    ): Promise<any> {
        const partTypes = ['department', 'unit', 'post'];
        return new Promise((resolve) => {
            this.dialog
                .open<OrgPositionDialogComponent>(OrgPositionDialogComponent, {
                    data: {
                        organization: this.organization,
                        node: node,
                        config,
                        selectedPositionKeys,
                        partTypes,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                })
                .afterClosed()
                .pipe(take(1))
                .subscribe((result) => {
                    resolve(result);
                });
        });
    }

    public openHierarchyDialog(
        node?: ECONode<any>,
        config = {
            selection: 'single' as 'single' | 'multiple',
            allowCreate: true,
        }
    ): Promise<any> {
        return new Promise((resolve) => {
            const partTypes = ['executive', 'division', 'department', 'section', 'unit', 'post'];

            // Use the most current organization from obService if available,
            // falling back to this.organization if not
            const currentOrg = this.obService.organization || this.organization;

            this.dialog
                .open<OrgHierarchyDialogComponent>(OrgHierarchyDialogComponent, {
                    data: {
                        organization: currentOrg,
                        node: node,
                        config,
                        partTypes,
                        isDeveloper: this.isDev,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                })
                .afterClosed()
                .pipe(take(1))
                .subscribe((result) => {
                    resolve(result);
                });
        });
    }

    public openOrgNotification(task: SecurityTask): void {
        this.dialog
            .open<OrgNotificationDialogComponent>(OrgNotificationDialogComponent, {
                data: {
                    organization: this.organization,
                    task: task,
                    isDeveloper: this.isDev,
                },
                height: '85%',
                width: '65%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openSecurityGroup(task: SecurityTask): void {
        this.dialog
            .open<OrgNotificationDialogComponent>(OrgNotificationDialogComponent, {
                data: {
                    organization: this.organization,
                    task: task,
                },
                height: '85%',
                width: '65%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openOrgNotificationList(): void {
        this.dialog
            .open<OrgNotificationListDialogComponent>(OrgNotificationListDialogComponent, {
                data: {
                    organization: this.organization,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                // console.log('dialog close result: ', result);
            });
    }

    public openSecurityGroupDialog(goToNewTab = false) {
        return new Promise<{
            succeeded: boolean;
            entity: OrgSecurityGroup;
        }>((resolve) => {
            this.dialog
                .open<OrgSecurityGroupComponent>(OrgSecurityGroupComponent, {
                    data: {
                        organization: this.organization,
                        goToNewTab: goToNewTab,
                    },
                    height: '85%',
                    width: '65%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                })
                .afterClosed()
                .pipe(take(1))
                .subscribe((result) => {
                    resolve(result);
                });
        });
    }

    /**
     * Open the Reorder Children dialog to allow drag-and-drop reordering of a parent's children.
     *
     * This dialog uses the atomic `reorderChildren` backend operation which:
     * 1. Updates all paths in a single transaction
     * 2. Prevents race conditions that occurred with individual updatePart calls
     * 3. Cascades path updates to all descendants
     *
     * @param parentPartKey - The partKey of the parent whose children will be reordered
     * @returns Promise that resolves with the result when dialog closes
     *
     * @see REORDER-CHILDREN-ARCHITECTURE.md for full design documentation
     */
    public openReorderChildrenDialog(
        parentPartKey: string
    ): Promise<{ success: boolean; newChildOrder?: string[] } | null> {
        return new Promise((resolve) => {
            this.dialog
                .open<OrgReorderChildrenDialogComponent>(OrgReorderChildrenDialogComponent, {
                    data: {
                        organization: this.organization,
                        parentPartKey: parentPartKey,
                    },
                    width: '500px',
                    maxWidth: '90vw',
                    maxHeight: '80vh',
                })
                .afterClosed()
                .pipe(take(1))
                .subscribe((result) => {
                    resolve(result);
                });
        });
    }

    /**
     * Open the Select Hat dialog for assigning a user to a hat on a position.
     * Used when a user is dragged from the user pool onto a position on the org board.
     *
     * The dialog shows:
     * - List of hats linked to the target position
     * - Option to select an existing hat
     * - Option to create a new hat if none exist
     *
     * @param node - The target position node
     * @param user - The user being assigned (from drag-drop)
     * @param hatsForPosition - Optional pre-filtered hats for this position
     * @param targetPartKey - Optional actual target position partKey (for nested parts)
     * @param targetPartName - Optional actual target position name (for nested parts)
     * @returns Promise with the dialog result
     */
    public openSelectHatDialog(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        node: ECONode<any>,
        user: { uid: string; name: string; email?: string },
        hatsForPosition?: OrgHat[],
        targetPartKey?: string,
        targetPartName?: string
    ): Promise<SelectHatDialogResult | undefined> {
        const dialogData: SelectHatDialogInput = {
            node,
            user,
            organization: this.organization!,
            hatsForPosition,
            targetPartKey,
            targetPartName,
        };

        return firstValueFrom(
            this.dialog
                .open<OrgSelectHatDialogComponent, SelectHatDialogInput, SelectHatDialogResult>(
                    OrgSelectHatDialogComponent,
                    {
                        data: dialogData,
                        width: '600px',
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                    }
                )
                .afterClosed()
        );
    }

    /**
     * Opens the approval dialog for a pending position assignment.
     * Accepts either a single approval record (legacy) or a bundle of records
     * (e.g. multiple pending approvals for the same user on the same post).
     * When a bundle is supplied, the dialog shows the union of all hats across
     * the records and approves/rejects each underlying record with its own subset.
     */
    public openApproveAssignmentDialog(
        approvalOrBundle: PendingPositionApproval | PendingPositionApproval[]
    ): Promise<{ action: 'approved' | 'rejected'; approvedHatKeys?: string[] } | null> {
        if (!this.organization) return Promise.resolve(null);

        const data: ApproveAssignmentDialogData = Array.isArray(approvalOrBundle)
            ? { approvals: approvalOrBundle, organization: this.organization }
            : { approval: approvalOrBundle, organization: this.organization };

        return firstValueFrom(
            this.dialog
                .open<OrgApproveAssignmentDialogComponent, ApproveAssignmentDialogData>(
                    OrgApproveAssignmentDialogComponent,
                    {
                        data,
                        width: '700px',
                        maxWidth: '90vw',
                        maxHeight: '85vh',
                    }
                )
                .afterClosed()
                .pipe(take(1))
        );
    }

    /**
     * Opens the "Manage Hats on Post" dialog. Lets an admin link or unlink
     * existing org hats to a single post via instant Firestore writes.
     *
     * @param post Object containing the post's partKey/key and name.
     */
    public openPostHatsManagerDialog(post: { partKey?: string; key?: string; name?: string }): void {
        if (!this.organization) return;
        const postKey = post?.partKey || post?.key;
        if (!postKey) {
            console.warn('[OrgDialogService] openPostHatsManagerDialog: missing post key', post);
            return;
        }

        this.dialog.open<OrgPostHatsManagerDialogComponent, PostHatsManagerDialogData>(
            OrgPostHatsManagerDialogComponent,
            {
                data: {
                    organization: this.organization,
                    postKey,
                    postName: post?.name || 'Post',
                },
                width: '640px',
                maxWidth: '92vw',
                maxHeight: '85vh',
                autoFocus: false,
            }
        );
    }
}
